// Feature-flag gate for the Company Profile REPORT redesign (the 3-tab profile
// screen at /companies/:id/profile — NOT the company search/selector, which has
// its own flag in Profiles/SelectorBeta/flag.js).
//
// Variant values: 'new' | 'classic'. Hard default is 'classic' (the shipped UI):
// flag off, PostHog blocked, or flags not loaded in time all resolve to classic,
// so nothing changes for users until the flag is turned on.
//
// Switch-back mechanisms, highest priority first:
//   1. URL override  ?cp_ui=new | old | classic | auto   (persists to localStorage;
//      'auto' clears the override and falls back to the flag)
//   2. localStorage  cp_ui_override = 'new' | 'classic'   (the user's remembered choice)
//   3. PostHog flag  'company-profile-redesign'  (boolean true, or variant 'new'/'test')
//   4. default 'classic'
//
// Users toggle between the two UIs themselves, and we remember the choice —
// exactly like the Beta selectors (see SelectorBeta/flag.js). setCompanyProfileVariant()
// records the choice as a PostHog person property (follows the account across
// devices; the flag's release conditions should rank it above any experiment
// bucket) AND in localStorage (instant, works even before/without PostHog config),
// then reloads so the gate re-renders. The "Try the beta version" invite on the
// classic header is ALWAYS shown (like the selectors' TryBetaPill) — it just hides
// once the user is already on the new UI.
import posthog from 'posthog-js'; // singleton — same instance main.js initialized

export const CP_FLAG = 'company-profile-redesign';
export const CP_OVERRIDE_KEY = 'cp_ui_override';       // localStorage
export const CP_OVERRIDE_PROP = 'company_profile_ui_override'; // PostHog person property

function normalize(v) {
  if (v === 'new' || v === 'beta' || v === true || v === 'true' || v === 'test') return 'new';
  if (v === 'old' || v === 'classic' || v === false || v === 'false') return 'classic';
  return null;
}

// Reads ?cp_ui= from the URL, persists it (or clears on 'auto'), and returns the
// resolved override — or null if there is none.
function readUrlOverride() {
  try {
    const raw = new URLSearchParams(window.location.search).get('cp_ui');
    if (raw == null) return null;
    if (raw === 'auto' || raw === 'reset') {
      localStorage.removeItem(CP_OVERRIDE_KEY);
      return null;
    }
    const norm = normalize(raw);
    if (norm) {
      try { localStorage.setItem(CP_OVERRIDE_KEY, norm); } catch (e) { /* ignore */ }
    }
    return norm;
  } catch (e) {
    return null;
  }
}

function readStoredOverride() {
  try {
    return normalize(localStorage.getItem(CP_OVERRIDE_KEY));
  } catch (e) {
    return null;
  }
}

export function resolveCompanyProfileVariant(timeoutMs = 1500) {
  // Manual overrides win and resolve synchronously.
  const override = readUrlOverride() || readStoredOverride();
  if (override) return Promise.resolve(override);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (v) => {
      if (settled) return;
      settled = true;
      resolve(normalize(v) === 'new' ? 'new' : 'classic');
    };
    try {
      const current = posthog.getFeatureFlag(CP_FLAG);
      // undefined = flags not evaluated yet this session; returning users hit
      // posthog-js's localStorage cache and resolve synchronously here.
      if (current !== undefined) return finish(current);
      posthog.onFeatureFlags(() => finish(posthog.getFeatureFlag(CP_FLAG)));
      setTimeout(() => finish('classic'), timeoutMs);
    } catch (e) {
      finish('classic');
    }
  });
}

// Record the user's explicit choice ('new' | 'classic') and switch. Persists to a
// PostHog person property (cross-device durability + ranks above the experiment
// bucket) and to localStorage (instant + works without PostHog). Resolves once the
// flag actually serves the chosen variant (or after a 4s timeout); callers reload
// AFTER this resolves so the gate doesn't read a stale cached value. Mirrors
// SelectorBeta/flag.js setSelectorOverride.
export function setCompanyProfileVariant(to) {
  const target = normalize(to) === 'new' ? 'new' : 'classic';
  try { localStorage.setItem(CP_OVERRIDE_KEY, target); } catch (e) { /* ignore */ }
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      posthog.capture('company_profile.variant_switched', { to: target });
      posthog.setPersonProperties({ [CP_OVERRIDE_PROP]: target });
      if (typeof posthog.setPersonPropertiesForFlags === 'function') {
        posthog.setPersonPropertiesForFlags({ [CP_OVERRIDE_PROP]: target }, false);
      }
      posthog.reloadFeatureFlags();
      const t0 = Date.now();
      const iv = setInterval(() => {
        let v;
        try { v = posthog.getFeatureFlag(CP_FLAG); } catch (e) { v = undefined; }
        if (normalize(v) === target || Date.now() - t0 > 4000) {
          clearInterval(iv);
          finish();
        }
      }, 150);
    } catch (e) {
      finish();
    }
  });
}

// Reload so the gate re-resolves the variant. Strips any ?cp_ui= override from the
// URL first — otherwise a user who reached the page via a ?cp_ui= link would have
// their just-made toggle choice overridden by the URL param on reload (URL is the
// highest-priority source). With it removed, the remembered choice (localStorage /
// person property) governs.
export function reloadWithProfileVariant() {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has('cp_ui')) {
      u.searchParams.delete('cp_ui');
      window.location.assign(u.pathname + u.search + u.hash);
      return;
    }
  } catch (e) {
    /* fall through to plain reload */
  }
  window.location.reload();
}
